import type { UseFormHandleSubmit } from "react-hook-form";
import type { Experiment } from "schemas/generated/experiment";
import type { ExperimentEditAction } from "./contexts/ExperimentEditContext";

import * as React from "react";

import EditableHeader from "@v1/features/experiments/EditableHeader";
import { useExperimentApi } from "@v1/features/experiments/utils";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { experimentUtils } from "@v2/trpc/routes/experiment";

type ExperimentEditableHeaderProps = {
  title: string;
  dispatchExperimentEdit: (action: ExperimentEditAction) => void;
  handleReactHookFormSubmit: UseFormHandleSubmit<Experiment, Experiment>;
  isLoadingRequiredData: boolean;
  experimentId: string;
};

export const ExperimentEditableHeader = ({
  title,
  isLoadingRequiredData,
  dispatchExperimentEdit,
  handleReactHookFormSubmit,
  experimentId,
}: ExperimentEditableHeaderProps) => {
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();

  const {
    update: { mutateAsync: update },
  } = useExperimentApi();

  const onSave = async (data: Experiment) => {
    await update(data);
  };

  const onTitleChange = handleReactHookFormSubmit(onSave);

  return (
    <EditableHeader
      title={title}
      onTitleChange={async (newName: string) => {
        if (newName) {
          dispatchExperimentEdit({
            type: "changeProperty",
            payload: { key: "name", value: newName, shouldDirty: false },
          });
          await onTitleChange();
          await experimentUtils.findDetail.invalidate({
            experimentId: experimentId,
            workspaceId: workspaceId ?? undefined,
            projectId,
          });
        }
      }}
      isLoading={isLoadingRequiredData}
    />
  );
};
